import { motion } from "framer-motion";
import { GraduationCap, Award, Network } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import SEOHead from "@/components/SEOHead";
import { Card, CardContent } from "@/components/ui/card";
import qualifizierungHero from "@/assets/qualifizierung-hero.jpg";

const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
};

const staggerContainer = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.15 } },
};

const offerings = [
  {
    icon: GraduationCap,
    title: "Fort- & Weiterbildung",
    description:
      "Wir gestalten praxisnahe Fort- und Weiterbildungen zu Themen wie Konfliktmanagement, Paradoxiemanagement, Implementierung von KI in Organisationen, uvm. All das bauen wir wissenschaftlich fundiert, didaktisch gehirngerecht aufbereitet und sofort in Ihrem Arbeitsalltag anwendbar. Damit Sie Ihre Kompetenzen nachhaltig stärken und aktuellen Herausforderungen gerecht werden.",
  },
  {
    icon: Award,
    title: "Führungskräfteprogramme",
    description:
      "Moderne Führung erfordert Haltung mehr als Methodenkoffer. Wir entwickeln maßgeschneiderte Programme für angehende und erfahrene Führungskräfte – von Selbstführung bis zur strategischen Organisationsführung.",
  },
  {
    icon: Network,
    title: "Organisationsentwicklung",
    description:
      "Unser Angebot für alle, die Ihre Organisation oder die Ihrer Kunden auf ein neues Level heben möchten. Wir geben unser Wissen und unsere Modelle weiter, um Sie zu einer souveränen Begleitung komplexer organisationaler Veränderungsprozesse zu befähigen.",
  },
];

const serviceJsonLd = {
  "@context": "https://schema.org",
  "@type": "Service",
  name: "Qualifizierung & Weiterbildung",
  description: "Praxisnahe Fort- und Weiterbildungen, Führungskräfteprogramme und Organisationsentwicklungs-Ausbildungen – wissenschaftlich fundiert und gehirngerecht aufbereitet.",
  provider: {
    "@type": "Organization",
    name: "Leadership Focus GmbH",
    url: "https://leadership-focus.de",
  },
  areaServed: [
    { "@type": "Country", name: "Deutschland" },
    { "@type": "Country", name: "Österreich" },
    { "@type": "Country", name: "Schweiz" },
  ],
  serviceType: "Qualifizierung",
};

const Qualifizierung = () => (
  <>
    <SEOHead title="Qualifizierung – Leadership Focus" description="Qualifizierungsprogramme und Workshops für nachhaltige Kompetenzentwicklung. Leadership Focus München." canonical="https://leadership-focus.de/portfolio/qualifizierung" />
    <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(serviceJsonLd) }} />
    <Header />
    <main className="py-20 md:py-28">
      <div className="container">
        <motion.div
          initial="hidden"
          animate="visible"
          variants={staggerContainer}
          className="text-center mb-14"
        >
          <motion.h1 variants={fadeInUp} className="text-4xl md:text-5xl font-serif font-bold text-foreground mb-4">
            Qualifizierung
          </motion.h1>
          <motion.p variants={fadeInUp} className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Qualifizierungsprogramme und Workshops für nachhaltige Kompetenzentwicklung auf allen Ebenen.
          </motion.p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.3 }}
          className="mb-14 rounded-2xl overflow-hidden shadow-lg"
        >
          <img
            src={qualifizierungHero}
            alt="Leadership Focus Qualifizierungsteam"
            className="w-full h-80 md:h-[28rem] object-cover object-top"
          />
        </motion.div>

        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.1 }}
          variants={staggerContainer}
          className="grid grid-cols-1 md:grid-cols-3 gap-6"
        >
          {offerings.map((item) => (
            <motion.div key={item.title} variants={fadeInUp}>
              <Card className="h-full border-border/50 hover:border-primary/30 hover:shadow-lg transition-all duration-300 group">
                <CardContent className="p-8">
                  <div className="mb-5 flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10 text-primary group-hover:bg-primary group-hover:text-primary-foreground transition-colors duration-300">
                    <item.icon className="h-6 w-6" />
                  </div>
                  <h2 className="text-xl font-semibold text-foreground mb-2 font-serif">{item.title}</h2>
                  <p className="text-muted-foreground leading-relaxed">{item.description}</p>
                  {item.title === "Organisationsentwicklung" && (
                    <div className="mt-4 space-y-3 text-sm text-muted-foreground leading-relaxed border-t border-border/50 pt-4">
                      <p>
                        Gemeinsam mit der teamsysplus®AKADEMIE führen wir eine Ausbildung zur neurowissenschaftlich-systemischen Organisationsentwickler:in durch. Weitere Informationen dazu finden Sie{" "}
                        <a href="https://www.teamsysplus-akademie.de/organisationsbegleitung/" target="_blank" rel="noopener noreferrer" className="text-primary underline hover:text-primary/80 transition-colors">hier</a>.
                      </p>
                      <p>
                        Wir entwickeln und arbeiten mit verschiedenen KI-Tools, u.a. dem{" "}
                        <a href="https://chatgpt.com/g/g-AMccWDOB2-isbgpt-die-systemische-ki-assistenz-des-isb" target="_blank" rel="noopener noreferrer" className="text-primary underline hover:text-primary/80 transition-colors">isbGPT</a>
                        {" "}– der KI-Sparringspartner des{" "}
                        <a href="https://www.isb-w.eu/de/" target="_blank" rel="noopener noreferrer" className="text-primary underline hover:text-primary/80 transition-colors">isb</a>.
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </main>
    <Footer />
  </>
);

export default Qualifizierung;
